/**
 * Pit ERPNext — Nextcloud Location / Web View: the "New" controls (Gerald 2026-10).
 *
 * Adds a "+ New" button to the LEFT of the breadcrumb bar of the file explorer
 * (page: /nextcloud-location/<location> and /nextcloud/<web view>):
 *   - upload one or MANY files into the folder currently open (per-file
 *     progress bar, per-file status: green check when finished, retry on error)
 *   - create a subfolder
 *   - crop / rotate image files before they are uploaded
 *
 * The controls are shown ONLY when the resolved location is NOT read-only
 * (Nextcloud Location -> read_only / Nextcloud Web View -> read_only; the list
 * endpoint reports it).
 *
 * Every name (file or folder) is cleaned with the SAME rules the server uses
 * (nc_connection.nc_clean_item_name, delivered as `name_rules` with the list
 * response): path separators, ":" "?" "*" "<" ">" '"' "|", control characters,
 * leading/trailing dots and spaces are removed, ".part" is refused, Windows
 * device names get an underscore and the name is cut at 125 characters.
 *
 * This file is loaded AFTER nextcloud_web_view.js (both `defer`) and uses the
 * host API it exposes on window.NCWeb.
 */
(function () {
	"use strict";

	// ----- host API (nextcloud_web_view.js) -----
	var host = null;
	// Fallback rules until the first list response delivers the real ones.
	var nameRules = {
		max_length: 125,
		forbidden_chars: '\\/:*?"<>|',
		forbidden_extensions: ["part"],
		reserved_basenames: []
	};

	// Upload limit of the mountpoint (Nextcloud Mountpoint -> max_upload_size,
	// MB). 0 = no mountpoint limit; the server request limit (nginx
	// client_max_body_size) then still refuses bigger files with 413.
	var maxUploadMb = 0;
	function currentLimitBytes() { return maxUploadMb ? maxUploadMb * 1024 * 1024 : 0; }

	var IMAGE_EXT = ["png", "jpg", "jpeg", "webp", "gif", "bmp"];

	// ----- icons (MDI paths) -----
	var ICON_PLUS = "M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z";
	var ICON_UPLOAD = "M9,16V10H5L12,3L19,10H15V16H9M5,20V18H19V20H5Z";
	var ICON_NEWFOLDER = "M20,18H4V8H20M20,6H12L10,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V8C22,6.89 21.1,6 20,6M19,11H15V9H13V11H11V13H13V15H15V13H19V11Z";
	var ICON_CHECK = "M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z";
	var ICON_ALERT = "M13,13H11V7H13M13,17H11V15H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z";
	var ICON_CLOSE = "M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z";
	var ICON_SCISSORS = "M9.64,7.64C9.87,7.14 10,6.59 10,6A4,4 0 0,0 6,2A4,4 0 0,0 2,6A4,4 0 0,0 6,10C6.59,10 7.14,9.87 7.64,9.64L10,12L7.64,14.36C7.14,14.13 6.59,14 6,14A4,4 0 0,0 2,18A4,4 0 0,0 6,22A4,4 0 0,0 10,18C10,17.41 9.87,16.86 9.64,16.36L12,14L19,21H22V20L9.64,7.64M6,8A2,2 0 0,1 4,6A2,2 0 0,1 6,4A2,2 0 0,1 8,6A2,2 0 0,1 6,8M6,20A2,2 0 0,1 4,18A2,2 0 0,1 6,16A2,2 0 0,1 8,18A2,2 0 0,1 6,20M12,13.5A0.5,0.5 0 0,1 11.5,13A0.5,0.5 0 0,1 12,12.5A0.5,0.5 0 0,1 12.5,13A0.5,0.5 0 0,1 12,13.5M19,3L14.83,7.17L16.24,8.59L21,3.83V3H19Z";
	var ICON_ROT_LEFT = "M12.5,8C9.85,8 7.45,9 5.6,10.6L2,7V16H11L7.38,12.38C8.77,11.22 10.54,10.5 12.5,10.5C16.04,10.5 19.05,12.81 20.1,16L22.47,15.22C21.08,11.03 17.15,8 12.5,8Z";
	var ICON_ROT_RIGHT = "M18.4,10.6C16.55,9 14.15,8 11.5,8C6.85,8 2.92,11.03 1.54,15.22L3.9,16C4.95,12.81 7.95,10.5 11.5,10.5C13.45,10.5 15.23,11.22 16.62,12.38L13,16H22V7L18.4,10.6Z";
	var ICON_FILE = "M13,9H18.5L13,3.5V9M6,2H14L20,8V20A2,2 0 0,1 18,22H6C4.89,22 4,21.1 4,20V4C4,2.89 4.89,2 6,2M15,18V16H6V18H15M18,14V12H6V14H18Z";
	var ICON_IMG = "M8.5,13.5L11,16.5L14.5,12L19,18H5M21,19V5C21,3.89 20.1,3 19,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19Z";
	var ICON_CHEVRON = "M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z";

	function t(src) { return host ? host.t(src) : src; }
	function el(tag, cls, text) { return host.el(tag, cls, text); }
	function svgEl(path, fill, viewBox) { return host.svgEl(path, fill, viewBox); }
	function icon(path, fill) {
		var svg = svgEl(path, fill || "currentColor");
		svg.setAttribute("class", (svg.getAttribute("class") || "") + " ncweb-x-icon");
		return svg;
	}

	// ------------------------------------------------------------------
	// Name cleaning — mirror of nc_connection.nc_clean_item_name()
	// ------------------------------------------------------------------
	// Split a file name into base + extension. The extension KEEPS its leading
	// dot and is immutable in the upload dialog (Gerald 2026-10); a leading dot
	// (dot files like ".gitignore") is a name part, not an extension.
	function extSplit(name) {
		var n = String(name == null ? "" : name);
		var i = n.lastIndexOf(".");
		if (i <= 0 || i === n.length - 1) return { base: n, ext: "" };
		return { base: n.slice(0, i), ext: n.slice(i) };
	}

	function cleanName(raw) {
		var rules = nameRules || {};
		var forbidden = rules.forbidden_chars || '\\/:*?"<>|';
		var reserved = rules.reserved_basenames || [];
		var forbiddenExt = rules.forbidden_extensions || ["part"];
		var maxLen = rules.max_length || 125;

		var s = String(raw == null ? "" : raw);
		try { s = s.normalize("NFC"); } catch (e) { /* ignore */ }
		// control characters + bidi overrides (Nextcloud/Windows refuse those)
		s = s.replace(/[\u0000-\u001f\u007f\u200e\u200f\u202a-\u202e]/g, "");
		var out = "";
		for (var i = 0; i < s.length; i++) {
			if (forbidden.indexOf(s.charAt(i)) === -1) out += s.charAt(i);
		}
		s = out.replace(/\s+/g, " ").replace(/^[.\s]+/, "").replace(/[.\s]+$/, "");
		if (s === "." || s === "..") s = "";
		var m = s.match(/^(.*)\.([^.]*)$/);
		if (m && forbiddenExt.indexOf(m[2].toLowerCase()) >= 0) s = m[1];
		if (reserved.indexOf(s.toLowerCase()) >= 0) s = s + "_";
		if (s.length > maxLen) {
			var mm = s.match(/^(.*)\.([^.]*)$/);
			if (mm && mm[2].length <= 20) {
				s = mm[1].slice(0, Math.max(maxLen - mm[2].length - 1, 1)).replace(/[.\s]+$/, "") + "." + mm[2];
			} else {
				s = s.slice(0, maxLen).replace(/[.\s]+$/, "");
			}
		}
		return s;
	}

	function isImageName(name) {
		var ext = String(name || "").split(".").pop().toLowerCase();
		return IMAGE_EXT.indexOf(ext) >= 0;
	}

	function fmtSize(bytes) {
		var n = Number(bytes || 0);
		if (n < 1024) return n + " B";
		if (n < 1024 * 1024) return (n / 1024).toFixed(0) + " KB";
		if (n < 1024 * 1024 * 1024) return (n / 1024 / 1024).toFixed(1) + " MB";
		return (n / 1024 / 1024 / 1024).toFixed(2) + " GB";
	}

	// ==================================================================
	// "+ New" button in the breadcrumb bar (left side)
	// ==================================================================
	var newWrap = null;
	var newBtn = null;
	var newMenu = null;
	var menuOpen = false;

	function buildNewButton() {
		var headerEl = document.querySelector("#ncweb-breadcrumb");
		headerEl = headerEl && headerEl.closest(".ncweb-header");
		if (!headerEl || document.querySelector(".ncweb-new")) return;

		newWrap = el("div", "ncweb-new");

		newBtn = el("button", "ncweb-new-btn");
		newBtn.type = "button";
		newBtn.appendChild(icon(ICON_PLUS));
		var label = el("span", "ncweb-new-label", t("New"));
		newBtn.appendChild(label);
		newBtn.title = t("New");
		newBtn.setAttribute("aria-haspopup", "true");

		newMenu = el("div", "ncweb-new-menu");
		newMenu.style.display = "none";
		newMenu.appendChild(newMenuItem("upload", ICON_UPLOAD, t("Upload files")));
		newMenu.appendChild(newMenuItem("folder", ICON_NEWFOLDER, t("New folder")));

		newBtn.addEventListener("click", function (ev) {
			ev.preventDefault();
			ev.stopPropagation();
			setMenu(!menuOpen);
		});
		document.addEventListener("click", function (ev) {
			if (!newWrap.contains(ev.target)) setMenu(false);
		});

		newWrap.appendChild(newBtn);
		newWrap.appendChild(newMenu);
		// LEFT of the breadcrumb path (Gerald: "links neben den Breadcrumbs")
		headerEl.insertBefore(newWrap, headerEl.firstChild);
	}

	function newMenuItem(action, path, text) {
		var b = el("button", "ncweb-new-menu-item");
		b.type = "button";
		b.appendChild(icon(path));
		b.appendChild(el("span", null, text));
		b.addEventListener("click", function (ev) {
			ev.preventDefault();
			ev.stopPropagation();
			setMenu(false);
			if (action === "upload") openUploadDialog();
			else openFolderDialog();
		});
		return b;
	}

	function setMenu(open) {
		menuOpen = !!open;
		if (newMenu) newMenu.style.display = menuOpen ? "block" : "none";
		if (newBtn) newBtn.classList.toggle("ncweb-new-btn-active", menuOpen);
	}

	function syncNewVisibility() {
		if (!newWrap) return;
		var show = !!host && !host.isReadOnly();
		newWrap.style.display = show ? "" : "none";
	}

	// ==================================================================
	// Generic modal shell (Nextcloud/Frappe-like card on a dim backdrop)
	// ==================================================================
	function openModal(title, cls, onClose) {
		var backdrop = el("div", "ncweb-modal-backdrop");
		var card = el("div", "ncweb-modal" + (cls ? " " + cls : ""));
		var head = el("div", "ncweb-modal-head");
		head.appendChild(el("h4", "ncweb-modal-title", title));
		var close = el("button", "ncweb-modal-close");
		close.type = "button";
		close.appendChild(icon(ICON_CLOSE));
		close.title = t("Close");
		close.setAttribute("aria-label", t("Close"));
		head.appendChild(close);
		var body = el("div", "ncweb-modal-body");
		var foot = el("div", "ncweb-modal-foot");
		card.appendChild(head);
		card.appendChild(body);
		card.appendChild(foot);
		backdrop.appendChild(card);
		document.body.appendChild(backdrop);
		document.body.classList.add("ncweb-modal-open");

		function destroy() {
			document.body.classList.remove("ncweb-modal-open");
			if (backdrop.parentNode) backdrop.parentNode.removeChild(backdrop);
			document.removeEventListener("keydown", onKey);
			if (typeof onClose === "function") {
				try { onClose(); } catch (e) { /* ignore */ }
			}
		}
		function onKey(ev) { if (ev.key === "Escape") destroy(); }
		document.addEventListener("keydown", onKey);
		close.addEventListener("click", function (ev) { ev.preventDefault(); destroy(); });

		return { backdrop: backdrop, card: card, body: body, foot: foot, close: destroy, closeBtn: close };
	}

	function button(label, variant, onClick) {
		var b = el("button", "ncweb-btn ncweb-btn-" + (variant || "secondary"), label);
		b.type = "button";
		if (onClick) b.addEventListener("click", function (ev) { ev.preventDefault(); onClick(ev); });
		return b;
	}

	// ==================================================================
	// Upload dialog
	// ==================================================================
	var uploadItems = [];   // { file, blob, name, state, progress, node, ... }
	var uploadRoute = "";
	var uploadRunning = false;

	function openUploadDialog() {
		if (host.isReadOnly()) return;
		uploadRoute = host.getRoute() || "";
		uploadItems = [];
		uploadRunning = false;

		var dlg = openModal(t("Upload files"), "ncweb-upload-modal");

		// --- drop zone + file picker ---
		var drop = el("div", "ncweb-drop");
		var dropIcon = svgEl(ICON_UPLOAD, "currentColor");
		dropIcon.setAttribute("class", "ncweb-x-icon ncweb-drop-icon");
		drop.appendChild(dropIcon);
		drop.appendChild(el("p", "ncweb-drop-title", t("Drag files here or click to select")));

		var input = el("input", "ncweb-drop-input");
		input.type = "file";
		input.multiple = true;
		input.style.display = "none";
		drop.appendChild(input);

		var list = el("ul", "ncweb-upload-list");
		var summary = el("span", "ncweb-upload-summary");

		drop.addEventListener("click", function (ev) {
			if (ev.target === input) return;
			input.click();
		});
		input.addEventListener("change", function () {
			addFiles(input.files);
			input.value = "";
		});
		["dragenter", "dragover"].forEach(function (evName) {
			drop.addEventListener(evName, function (ev) {
				ev.preventDefault();
				ev.stopPropagation();
				drop.classList.add("ncweb-drop-active");
			});
		});
		["dragleave", "drop"].forEach(function (evName) {
			drop.addEventListener(evName, function (ev) {
				ev.preventDefault();
				ev.stopPropagation();
				drop.classList.remove("ncweb-drop-active");
			});
		});
		drop.addEventListener("drop", function (ev) {
			var dt = ev.dataTransfer;
			if (dt && dt.files && dt.files.length) addFiles(dt.files);
		});
		// dropping anywhere on the dialog also works
		dlg.body.addEventListener("dragover", function (ev) { ev.preventDefault(); });
		dlg.body.addEventListener("drop", function (ev) {
			ev.preventDefault();
			var dt = ev.dataTransfer;
			if (dt && dt.files && dt.files.length) addFiles(dt.files);
		});

		dlg.body.appendChild(drop);
		dlg.body.appendChild(list);

		var startBtn = button(t("Start upload"), "primary", function () {
			startUploads();
		});
		var closeBtn = button(t("Close"), "secondary", function () {
			if (uploadRunning) {
				if (!window.confirm(t("An upload is still running. Close anyway?"))) return;
				uploadItems.forEach(function (it) { if (it.xhr) it.xhr.abort(); });
			}
			dlg.close();
		});
		dlg.foot.appendChild(summary);
		dlg.foot.appendChild(closeBtn);
		dlg.foot.appendChild(startBtn);

		updateSummary();

		// --- helpers of the dialog ---
		var tooLarge = [];
		function addFiles(fileList) {
			if (uploadRunning) return;
			for (var i = 0; i < fileList.length; i++) {
				var f = fileList[i];
				var limit = currentLimitBytes();
				if (limit && f.size > limit) {
					tooLarge.push(f.name);
					continue;
				}
				var parts = extSplit(cleanName(f.name) || cleanName("file") || "file");
				var item = {
					file: f,
					blob: f,
					name: parts.base + parts.ext,
					nameBase: parts.base,
					ext: parts.ext,
					original: f.name,
					size: f.size,
					state: "ready",
					progress: 0,
					cropped: false
				};
				uploadItems.push(item);
				item.node = renderRow(item);
				list.appendChild(item.node);
			}
			updateSummary();
			if (tooLarge.length) {
				var names = tooLarge.join(", ");
				tooLarge = [];
				dlg.card.querySelectorAll(".ncweb-modal-error").forEach(function (n) { n.remove(); });
				var box = el("div", "ncweb-modal-error",
					t("File is larger than the mountpoint limit.") + " (" + names + ")");
				dlg.body.appendChild(box);
			}
		}

		function renderRow(item) {
			var row = el("li", "ncweb-up-row");

			var thumb = el("div", "ncweb-up-thumb");
			if (isImageName(item.name) && item.state !== "done") {
				try {
					item.thumbUrl = URL.createObjectURL(item.blob);
					var img = document.createElement("img");
					img.src = item.thumbUrl;
					thumb.appendChild(img);
				} catch (e) { /* ignore */ }
			} else {
				thumb.appendChild(icon(isImageName(item.name) ? ICON_IMG : ICON_FILE, "#6f6f6f"));
			}
			row.appendChild(thumb);

			var main = el("div", "ncweb-up-main");
			// Name field: the EXTENSION is immutable (Gerald 2026-10) - the
			// input carries the BASE NAME only and item.name stays base + ext.
			// The suffix is deliberately NOT shown in this dialog (Gerald: the
			// info is not needed here); the tooltip carries the full name.
			var nameWrap = el("div", "ncweb-up-name-wrap");
			var nameInput = el("input", "ncweb-up-name");
			nameInput.type = "text";
			nameInput.value = item.nameBase;
			nameInput.title = item.name;
			nameInput.addEventListener("input", function () {
				var cleaned = cleanName(nameInput.value);
				item.nameBase = cleaned;
				item.name = cleaned + item.ext;
				nameInput.title = item.name;
				if (cleaned.length !== nameInput.value.length) {
					// keep the caret at the end when characters were dropped
					nameInput.value = cleaned;
				}
				nameInput.classList.toggle("ncweb-invalid", !cleaned);
				updateSummary();
			});
			nameInput.addEventListener("blur", function () {
				if (!cleanName(nameInput.value)) {
					var fallback = extSplit(cleanName(item.original)).base
						|| cleanName("file") || "file";
					nameInput.value = fallback;
					item.nameBase = fallback;
					item.name = fallback + item.ext;
					nameInput.title = item.name;
				}
			});
			item.nameInput = nameInput;
			nameWrap.appendChild(nameInput);
			main.appendChild(nameWrap);

			var meta = el("div", "ncweb-up-meta");
			meta.appendChild(el("span", "ncweb-up-size", fmtSize(item.size)));
			if (item.original !== item.name) {
				meta.appendChild(el("span", "ncweb-up-note", t("File name was adjusted.")));
			}
			main.appendChild(meta);

			var bar = el("div", "ncweb-up-bar");
			var fill = el("span", "ncweb-up-bar-fill");
			bar.appendChild(fill);
			main.appendChild(bar);
			item.barFill = fill;
			// Progress bar stays hidden until the upload actually starts
			// (Gerald 2026-10) - setStatus toggles ncweb-up-bar-active.
			item.barEl = bar;
			item.metaEl = meta;
			row.appendChild(main);

			var actions = el("div", "ncweb-up-actions");
			if (isImageName(item.name)) {
				var cropBtn = el("button", "ncweb-up-icon-btn");
				cropBtn.type = "button";
				cropBtn.appendChild(icon(ICON_SCISSORS));
				cropBtn.title = t("Crop image");
				cropBtn.setAttribute("aria-label", t("Crop image"));
				cropBtn.addEventListener("click", function (ev) {
					ev.preventDefault();
					if (item.state === "uploading" || item.state === "done") return;
					openCropper(item);
				});
				actions.appendChild(cropBtn);
				item.cropBtn = cropBtn;
			}
			var delBtn = el("button", "ncweb-up-icon-btn ncweb-up-icon-btn-danger");
			delBtn.type = "button";
			delBtn.appendChild(icon(ICON_CLOSE));
			delBtn.title = t("Remove");
			delBtn.setAttribute("aria-label", t("Remove"));
			delBtn.addEventListener("click", function (ev) {
				ev.preventDefault();
				if (item.state === "uploading") return;
				if (item.thumbUrl) { try { URL.revokeObjectURL(item.thumbUrl); } catch (e) { /* ignore */ } }
				if (row.parentNode) row.parentNode.removeChild(row);
				uploadItems = uploadItems.filter(function (x) { return x !== item; });
				updateSummary();
			});
			actions.appendChild(delBtn);
			row.appendChild(actions);
			item.delBtn = delBtn;

			// status column (right side): progress %, green check / red alert
			var status = el("div", "ncweb-up-status");
			row.appendChild(status);
			item.statusEl = status;

			return row;
		}

		function setStatus(item, state, text) {
			item.state = state;
			item.statusEl.textContent = "";
			item.statusEl.classList.remove("ncweb-up-status-done", "ncweb-up-status-error");
			// drop a previous error text (retry) before showing the new state
			Array.prototype.slice.call(item.metaEl.querySelectorAll(".ncweb-up-error"))
				.forEach(function (n) { n.parentNode.removeChild(n); });
			// Crop + remove are useless once the upload runs: hide them, their
			// space becomes the status spot for the spinner / green check
			// (Gerald 2026-10). On error both come back (drop a failed file).
			var hideActions = state === "uploading" || state === "done";
			if (item.cropBtn) item.cropBtn.style.display = hideActions ? "none" : "";
			if (item.delBtn) item.delBtn.style.display = hideActions ? "none" : "";
			if (state === "uploading") {
				// Spinner exactly where the green check appears later; the
				// percentage stays as small text. No "Saving…" note (Gerald).
				item.statusEl.appendChild(el("span", "ncweb-spinner"));
				item.statusEl.appendChild(el("span", "ncweb-up-pct",
					Math.round(item.progress * 100) + " %"));
			} else if (state === "done") {
				item.statusEl.classList.add("ncweb-up-status-done");
				item.statusEl.appendChild(icon(ICON_CHECK, "#2ea44f"));
			} else if (state === "error") {
				item.statusEl.classList.add("ncweb-up-status-error");
				item.statusEl.appendChild(icon(ICON_ALERT, "#dc5047"));
				if (text) {
					var err = el("span", "ncweb-up-error", text);
					item.metaEl.appendChild(err);
				}
			} else if (state === "ready") {
				item.statusEl.textContent = "";
			}
			item.barFill.style.width = (state === "done" ? 100 : Math.round(item.progress * 100)) + "%";
			item.barFill.classList.toggle("ncweb-up-bar-done", state === "done");
			// Bar appears with the upload, not before (Gerald 2026-10).
			if (item.barEl) item.barEl.classList.toggle("ncweb-up-bar-active", state !== "ready");
		}
		item_setStatus = setStatus;

		function updateSummary() {
			var total = uploadItems.length;
			var done = uploadItems.filter(function (i) { return i.state === "done"; }).length;
			var failed = uploadItems.filter(function (i) { return i.state === "error"; }).length;
			var bytes = uploadItems.reduce(function (sum, i) { return sum + (i.size || 0); }, 0);
			var parts = [];
			if (!total) parts.push(t("No files selected"));
			else parts.push(total === 1
			? t("1 file")
			: t("{0} files").replace("{0}", String(total)));
			if (done || failed) parts.push(done + " " + t("uploaded") + (failed ? " · " + failed + " " + t("failed") : ""));
			summary.textContent = parts.join(" · ");
			startBtn.disabled = uploadRunning || !uploadItems.some(function (i) {
				return (i.state === "ready" || i.state === "error") && !!cleanName(i.name);
			});
			startBtn.classList.toggle("ncweb-btn-disabled", startBtn.disabled);
		}
		item_updateSummary = updateSummary;

		function startUploads() {
			if (uploadRunning) return;
			uploadRunning = true;
			startBtn.disabled = true;
			var queue = uploadItems.filter(function (i) {
				return (i.state === "ready" || i.state === "error") && !!cleanName(i.name);
			});
			var idx = 0;
			function next() {
				if (idx >= queue.length) {
					uploadRunning = false;
					updateSummary();
					var failed = uploadItems.filter(function (i) { return i.state === "error"; });
					if (!failed.length) {
						// All files uploaded: the dialog closes ITSELF with a
						// short delay (the green checks stay readable) and the
						// folder list refreshes behind it. host.reload() alone
						// only refreshes the listing - the modal lives on the
						// body, so it must be closed explicitly (Gerald).
						window.setTimeout(function () {
							dlg.close();
							host.reload();
						}, 1200);
					}
					return;
				}
				uploadOne(queue[idx++], next);
			}
			next();
		}
		item_startUploads = startUploads;

		function uploadOne(item, done) {
			item.nameInput.disabled = true;
			item.progress = 0;
			setStatus(item, "uploading");
			var fd = new FormData();
			fd.append("file", item.blob, item.name);
			fd.append("view_id", host.getViewId());
			fd.append("route", uploadRoute);

			var xhr = new XMLHttpRequest();
			item.xhr = xhr;
			xhr.open("POST", host.curl() + host.getUploadMethod(), true);
			xhr.setRequestHeader("X-Frappe-CSRF-Token", host.csrf());
			xhr.upload.onprogress = function (ev) {
				if (ev.lengthComputable) {
					item.progress = ev.loaded / ev.total;
					setStatus(item, "uploading");
				}
			};
			xhr.onload = function () {
				item.xhr = null;
				var res = null;
				try { res = JSON.parse(xhr.responseText); } catch (e) { res = null; }
				if (xhr.status === 200 && res && res.message && res.message.ok) {
					setStatus(item, "done");
					if (res.message.renamed) {
						// Server renamed the file (name collision): keep the
						// locked-extension model in sync (Gerald 2026-10).
						var rp = extSplit(res.message.file_name);
						item.name = rp.base + rp.ext;
						item.nameBase = rp.base;
						item.ext = rp.ext;
						item.nameInput.value = rp.base;
						item.metaEl.appendChild(el("span", "ncweb-up-note",
							t("A file with this name existed - saved as") + " " + res.message.file_name));
					}
					item.nameInput.disabled = false;
					done();
					return;
				}
				var msg = res ? host.exceptionMessage(res) : (xhr.status === 413 ? t("File is too large.") : t("Upload failed"));
				setStatus(item, "error", msg);
				item.nameInput.disabled = false;
				updateSummary();
				done();
			};
			xhr.onerror = function () {
				item.xhr = null;
				setStatus(item, "error", t("Upload failed"));
				item.nameInput.disabled = false;
				updateSummary();
				done();
			};
			xhr.send(fd);
		}
	}

	// Dialog internals exposed for the cropper (same file scope)
	var item_setStatus = null;
	var item_updateSummary = null;
	var item_startUploads = null;

	// ==================================================================
	// New folder dialog
	// ==================================================================
	function openFolderDialog() {
		if (host.isReadOnly()) return;
		var route = host.getRoute() || "";
		var dlg = openModal(t("New folder"), "ncweb-folder-modal");

		var wrap = el("div", "ncweb-field");
		wrap.appendChild(el("label", "ncweb-label", t("Folder name")));
		var input = el("input", "ncweb-input");
		input.type = "text";
		input.placeholder = t("Folder name");
		wrap.appendChild(input);
		var hint = el("p", "ncweb-hint", t("Characters that Nextcloud does not allow are removed automatically."));
		wrap.appendChild(hint);
		var preview = el("p", "ncweb-hint ncweb-hint-preview");
		wrap.appendChild(preview);
		dlg.body.appendChild(wrap);

		var createBtn = button(t("Create"), "primary", submit);
		dlg.foot.appendChild(button(t("Cancel"), "secondary", function () { dlg.close(); }));
		dlg.foot.appendChild(createBtn);

		function sync() {
			var cleaned = cleanName(input.value);
			createBtn.disabled = !cleaned;
			createBtn.classList.toggle("ncweb-btn-disabled", !cleaned);
			preview.textContent = cleaned && cleaned !== input.value
				? t("Will be created as") + ": " + cleaned
				: "";
		}
		input.addEventListener("input", sync);
		input.addEventListener("keydown", function (ev) { if (ev.key === "Enter") submit(); });
		sync();
		setTimeout(function () { input.focus(); }, 30);

		function submit() {
			var cleaned = cleanName(input.value);
			if (!cleaned) {
				input.classList.add("ncweb-invalid");
				return;
			}
			createBtn.disabled = true;
			createBtn.classList.add("ncweb-btn-disabled");
			var fd = new FormData();
			fd.append("view_id", host.getViewId());
			fd.append("route", route);
			fd.append("name", cleaned);
			fetch(host.curl() + host.getFolderMethod(), {
				method: "POST",
				credentials: "same-origin",
				headers: { "X-Frappe-CSRF-Token": host.csrf() },
				body: fd
			}).then(function (r) { return r.json(); }).then(function (res) {
				if (res && res.message && res.message.ok) {
					dlg.close();
					host.showAlert(t("Folder created") + ": " + res.message.name, "green");
					host.reload();
					return;
				}
				var msg = res ? host.exceptionMessage(res) : t("Folder could not be created.");
				frappe_msg(msg);
				createBtn.disabled = false;
				createBtn.classList.remove("ncweb-btn-disabled");
			}).catch(function (err) {
				frappe_msg(String(err));
				createBtn.disabled = false;
				createBtn.classList.remove("ncweb-btn-disabled");
			});
		}

		function frappe_msg(msg) {
			var box = dlg.card.querySelector(".ncweb-modal-error");
			if (!box) {
				box = el("div", "ncweb-modal-error");
				dlg.body.appendChild(box);
			}
			box.textContent = msg;
		}
	}

	// ==================================================================
	// Image cropper (canvas + draggable crop rectangle)
	// ==================================================================
	function openCropper(item) {
		var tempUrl = null;
		var dlg = openModal(t("Crop image"), "ncweb-crop-modal", function () {
			if (tempUrl) { try { URL.revokeObjectURL(tempUrl); } catch (e) { /* ignore */ } }
		});
		var stage = el("div", "ncweb-crop-stage");
		var canvas = document.createElement("canvas");
		canvas.className = "ncweb-crop-canvas";
		var overlay = el("div", "ncweb-crop-overlay");
		// four dimming panes (outside the crop rectangle) - they make the
		// selected area obvious, like the crop tool of a photo app
		var dimTop = el("div", "ncweb-crop-dim");
		var dimBottom = el("div", "ncweb-crop-dim");
		var dimLeft = el("div", "ncweb-crop-dim");
		var dimRight = el("div", "ncweb-crop-dim");
		var rect = el("div", "ncweb-crop-rect");
		// visible corner handles (the hit test is a 14px proximity check, this is
		// only the affordance - the user sees where he can grab)
		["nw", "ne", "sw", "se"].forEach(function (h) {
			var hEl = el("span", "ncweb-crop-handle ncweb-crop-handle-" + h);
			rect.appendChild(hEl);
		});
		overlay.appendChild(dimTop);
		overlay.appendChild(dimBottom);
		overlay.appendChild(dimLeft);
		overlay.appendChild(dimRight);
		overlay.appendChild(rect);
		stage.appendChild(canvas);
		stage.appendChild(overlay);
		dlg.body.appendChild(stage);
		var info = el("p", "ncweb-hint", "");
		dlg.body.appendChild(info);

		var rotateBtnL = button(t("Rotate left"), "secondary", function () { rotate(-90); });
		rotateBtnL.insertBefore(icon(ICON_ROT_LEFT), rotateBtnL.firstChild);
		rotateBtnL.classList.add("ncweb-btn-icon");
		var rotateBtnR = button(t("Rotate right"), "secondary", function () { rotate(90); });
		rotateBtnR.insertBefore(icon(ICON_ROT_RIGHT), rotateBtnR.firstChild);
		rotateBtnR.classList.add("ncweb-btn-icon");
		var resetBtn = button(t("Reset"), "secondary", function () { base = source; fit(); resetRect(); });
		var applyBtn = button(t("Apply"), "primary", apply);
		dlg.foot.appendChild(rotateBtnL);
		dlg.foot.appendChild(rotateBtnR);
		dlg.foot.appendChild(resetBtn);
		dlg.foot.appendChild(button(t("Cancel"), "secondary", function () { dlg.close(); }));
		dlg.foot.appendChild(applyBtn);

		var source = null;   // the original bitmap (Image or canvas)
		var base = null;     // current rotation base
		var scale = 1;       // natural px -> css px
		var crop = { x: 0, y: 0, w: 0, h: 0 }; // in natural px
		var startUrl = item.thumbUrl || null;

		var url = URL.createObjectURL(item.blob);
		tempUrl = url;
		var img = new Image();
		img.onload = function () {
			source = img;
			base = img;
			fit();
			resetRect();
		};
		img.onerror = function () {
			info.textContent = t("Preview could not be loaded.");
		};
		img.src = url;

		function fit() {
			var availW = Math.min(stage.clientWidth || 640, 900);
			// The canvas must fit the space the MODAL really has: window*0.9
			// minus head + foot chrome and the hint line. The former "60% of the
			// window height" ignored that chrome, so a tall (rotated or large)
			// image overflowed the scrollable body and slid UNDER the footer -
			// its lower part was invisible AND unreachable for the crop handles
			// (Gerald: large images).
			var head = dlg.card.querySelector(".ncweb-modal-head");
			var foot = dlg.card.querySelector(".ncweb-modal-foot");
			var chrome = (head ? head.offsetHeight : 52) + (foot ? foot.offsetHeight : 110);
			var availH = Math.max(200, Math.round(window.innerHeight * 0.9) - chrome - 60);
			scale = Math.min(availW / base.width, availH / base.height, 1);
			canvas.width = base.width;
			canvas.height = base.height;
			canvas.style.width = Math.round(base.width * scale) + "px";
			canvas.style.height = Math.round(base.height * scale) + "px";
			overlay.style.width = canvas.style.width;
			overlay.style.height = canvas.style.height;
			var ctx = canvas.getContext("2d");
			ctx.clearRect(0, 0, canvas.width, canvas.height);
			ctx.drawImage(base, 0, 0);
			drawRect();
		}

		function resetRect() {
			crop = { x: 0, y: 0, w: canvas.width, h: canvas.height };
			drawRect();
		}

		function drawRect() {
			var x = Math.round(crop.x * scale);
			var y = Math.round(crop.y * scale);
			var w = Math.round(crop.w * scale);
			var h = Math.round(crop.h * scale);
			var fullW = Math.round(canvas.width * scale);
			var fullH = Math.round(canvas.height * scale);
			rect.style.left = x + "px";
			rect.style.top = y + "px";
			rect.style.width = w + "px";
			rect.style.height = h + "px";
			// dim everything outside the crop rectangle
			dimTop.style.cssText = "position:absolute;left:0;top:0;width:" + fullW + "px;height:" + y + "px;background:rgba(0,0,0,0.45);pointer-events:none;";
			dimBottom.style.cssText = "position:absolute;left:0;top:" + (y + h) + "px;width:" + fullW + "px;height:" + Math.max(fullH - y - h, 0) + "px;background:rgba(0,0,0,0.45);pointer-events:none;";
			dimLeft.style.cssText = "position:absolute;left:0;top:" + y + "px;width:" + x + "px;height:" + h + "px;background:rgba(0,0,0,0.45);pointer-events:none;";
			dimRight.style.cssText = "position:absolute;left:" + (x + w) + "px;top:" + y + "px;width:" + Math.max(fullW - x - w, 0) + "px;height:" + h + "px;background:rgba(0,0,0,0.45);pointer-events:none;";
			info.textContent = Math.round(crop.w) + " × " + Math.round(crop.h) + " px";
		}

		function rotate(deg) {
			var w = base.width, h = base.height;
			// The ACTIVE crop turns with the image and is KEPT (Gerald 2026-10):
			// only its position and the swapped sides change, the selected
			// content stays selected. Rect corners map as
			//   clockwise:        (x,y) -> (h - y, x)
			//   counter-clockwise:(x,y) -> (y, w - x)
			var c = crop;
			var rc = deg > 0
				? { x: h - (c.y + c.h), y: c.x, w: c.h, h: c.w }
				: { x: c.y, y: w - (c.x + c.w), w: c.h, h: c.w };
			var tmp = document.createElement("canvas");
			// 90/270 swap the dimensions; 0/180 keep them. The former ternary
			// was INVERTED, so a 90° rotation drew the image into a canvas with
			// the OLD dimensions and cut off everything outside it (Gerald).
			var swap = deg % 180 !== 0;
			tmp.width = swap ? h : w;
			tmp.height = swap ? w : h;
			var ctx = tmp.getContext("2d");
			ctx.translate(tmp.width / 2, tmp.height / 2);
			ctx.rotate(deg * Math.PI / 180);
			ctx.drawImage(base, -w / 2, -h / 2);
			base = tmp;
			crop = rc;
			fit();
			// Rounding safety: never let the rect leave the bitmap.
			crop.x = Math.max(0, Math.min(crop.x, canvas.width - 1));
			crop.y = Math.max(0, Math.min(crop.y, canvas.height - 1));
			crop.w = Math.max(1, Math.min(crop.w, canvas.width - crop.x));
			crop.h = Math.max(1, Math.min(crop.h, canvas.height - crop.y));
			drawRect();
		}

		// --- drag / resize the crop rectangle ---
		var HANDLES = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];
		var drag = null;
		// Which handle sits under a point (natural px) - shared by the click
		// test and the hover cursor. Corners use a WIDER tolerance than the
		// edges: they were hard to grab with the former 14px square zone
		// (Gerald 2026-10).
		function hitTest(px, py) {
			var tol = 14 / scale;
			// A fixed 28px corner zone swallows the WHOLE side of flat or compact
			// crops (e.g. 588x40: top and bottom zones overlap), so every grab
			// snapped to a corner and the drag jumped corner to corner (Gerald,
			// flat images). Cap each corner tolerance at 35% of that side, so a
			// plain edge band always survives.
			var ctolX = Math.min(28 / scale, Math.max(6 / scale, crop.w * 0.35));
			var ctolY = Math.min(28 / scale, Math.max(6 / scale, crop.h * 0.35));
			var dLeft = Math.abs(px - crop.x);
			var dRight = Math.abs(px - (crop.x + crop.w));
			var dTop = Math.abs(py - crop.y);
			var dBottom = Math.abs(py - (crop.y + crop.h));
			var hx = Math.min(dLeft, dRight);
			var hy = Math.min(dTop, dBottom);
			var cx = dLeft <= dRight ? "w" : "e";
			var cy = dTop <= dBottom ? "n" : "s";
			// Corner: near a vertical AND a horizontal boundary.
			if (hx <= ctolX && hy <= ctolY) return cy + cx;
			// Plain edges; when both are in reach the NEARER one wins.
			if (hx <= tol && hy <= tol) return hx <= hy ? cx : cy;
			if (hy <= tol) return cy;
			if (hx <= tol) return cx;
			if (px > crop.x && px < crop.x + crop.w && py > crop.y && py < crop.y + crop.h) return "move";
			return null;
		}
		function cursorFor(handle) {
			if (handle === "move") return "move";
			if (handle === "nw" || handle === "se") return "nwse-resize";
			if (handle === "ne" || handle === "sw") return "nesw-resize";
			if (handle === "n" || handle === "s") return "ns-resize";
			if (handle === "e" || handle === "w") return "ew-resize";
			return "default";
		}
		overlay.addEventListener("pointerdown", function (ev) {
			ev.preventDefault();
			var box = overlay.getBoundingClientRect();
			var px = (ev.clientX - box.left) / scale;
			var py = (ev.clientY - box.top) / scale;
			var handle = hitTest(px, py);
			if (!handle) return;
			drag = { handle: handle, startX: px, startY: py, crop: { x: crop.x, y: crop.y, w: crop.w, h: crop.h } };
			overlay.setPointerCapture(ev.pointerId);
			overlay.classList.add("ncweb-crop-dragging");
			overlay.style.cursor = "grabbing";
		});
		overlay.addEventListener("pointermove", function (ev) {
			var box = overlay.getBoundingClientRect();
			var px = (ev.clientX - box.left) / scale;
			var py = (ev.clientY - box.top) / scale;
			if (!drag) {
				// Hover: show the resize cursor so the grab zones are findable.
				overlay.style.cursor = cursorFor(hitTest(px, py));
				return;
			}
			var dx = px - drag.startX;
			var dy = py - drag.startY;
			var c = drag.crop;
			var n = { x: c.x, y: c.y, w: c.w, h: c.h };
			if (drag.handle === "move") {
				n.x = Math.min(Math.max(0, c.x + dx), canvas.width - c.w);
				n.y = Math.min(Math.max(0, c.y + dy), canvas.height - c.h);
			} else {
				if (drag.handle.indexOf("w") >= 0) {
					var nx = Math.min(Math.max(0, c.x + dx), c.x + c.w - 10);
					n.w = c.x + c.w - nx;
					n.x = nx;
				}
				if (drag.handle.indexOf("e") >= 0) {
					n.w = Math.min(Math.max(10, c.w + dx), canvas.width - c.x);
				}
				if (drag.handle.indexOf("n") >= 0) {
					var ny = Math.min(Math.max(0, c.y + dy), c.y + c.h - 10);
					n.h = c.y + c.h - ny;
					n.y = ny;
				}
				if (drag.handle.indexOf("s") >= 0) {
					n.h = Math.min(Math.max(10, c.h + dy), canvas.height - c.y);
				}
			}
			crop = n;
			drawRect();
		});
		function endDrag() {
			drag = null;
			overlay.classList.remove("ncweb-crop-dragging");
			overlay.style.cursor = "";
		}
		overlay.addEventListener("pointerup", endDrag);
		overlay.addEventListener("pointercancel", endDrag);

		function apply() {
			var out = document.createElement("canvas");
			out.width = Math.max(1, Math.round(crop.w));
			out.height = Math.max(1, Math.round(crop.h));
			var ctx = out.getContext("2d");
			ctx.drawImage(base, crop.x, crop.y, crop.w, crop.h, 0, 0, out.width, out.height);

			var name = item.name;
			var ext = name.split(".").pop().toLowerCase();
			var type = "image/png";
			if (ext === "jpg" || ext === "jpeg") type = "image/jpeg";
			else if (ext === "webp") type = "image/webp";
			else if (ext === "png") type = "image/png";
			else type = "image/png";

			out.toBlob(function (blob) {
				if (!blob) return;
				item.blob = blob;
				item.size = blob.size;
				var newName = name;
				if (type === "image/jpeg" && ext !== "jpg" && ext !== "jpeg") {
					newName = name.replace(/\.[^.]*$/, "") + ".jpg";
				}
				// The crop may legitimately change the extension (png -> jpg),
				// so base + locked suffix are re-split here (Gerald 2026-10).
				var np = extSplit(newName);
				item.name = np.base + np.ext;
				item.nameBase = np.base;
				item.ext = np.ext;
				item.nameInput.value = np.base;
				item.cropped = true;
				// refresh size + thumbnail
				var sizeEl = item.metaEl.querySelector(".ncweb-up-size");
				if (sizeEl) sizeEl.textContent = fmtSize(item.size);
				var thumb = item.node.querySelector(".ncweb-up-thumb");
				if (thumb) {
					thumb.textContent = "";
					if (item.thumbUrl) { try { URL.revokeObjectURL(item.thumbUrl); } catch (e) { /* ignore */ } }
					item.thumbUrl = URL.createObjectURL(blob);
					var timg = document.createElement("img");
					timg.src = item.thumbUrl;
					thumb.appendChild(timg);
				}
				if (item.cropped && !item.metaEl.querySelector(".ncweb-up-cropped")) {
					item.metaEl.appendChild(el("span", "ncweb-up-cropped", t("cropped")));
				}
				item_setStatus(item, "ready");
				item_updateSummary();
				dlg.close();
			}, type, 0.92);
		}
	}

	// ==================================================================
	// Rename / Delete — the write actions in the three-dots menu
	// ==================================================================
	// Same guard gate as upload / new folder, but PER FOLDER: the listing
	// response delivers the effective rights of the open folder (Write Grant
	// table of the location, Gerald 2026-10), so a menu can offer Rename /
	// Delete in one folder and not in the next. The server refuses anyway -
	// the UI check only hides what cannot work.
	//
	// Both actions work on ONE item of the folder that is currently open:
	// the client sends the folder route + the plain item name, never a path,
	// so nothing can be moved or deleted outside the visible folder.

	function extendItemMenu(menuDropdown, entry, menuBtn) {
		if (!menuDropdown || !entry || !entry.name) return;
		if (!host.can || !host.can("rename") && !host.can("delete")) return;

		if (host.can("rename")) {
			var renameItem = el("div", "ncweb-menu-item", t("Rename"));
			renameItem.addEventListener("click", function (ev) {
				ev.preventDefault();
				ev.stopPropagation();
				host.closeMenu(menuDropdown, menuBtn);
				openRenameDialog(entry);
			});
			menuDropdown.appendChild(renameItem);
		}

		if (host.can("delete")) {
			var deleteItem = el("div", "ncweb-menu-item", t("Delete"));
			deleteItem.addEventListener("click", function (ev) {
				ev.preventDefault();
				ev.stopPropagation();
				host.closeMenu(menuDropdown, menuBtn);
				deleteItemConfirm(entry);
			});
			menuDropdown.appendChild(deleteItem);
		}
	}

	function openRenameDialog(entry) {
		if (!host.can("rename")) return;
		var route = host.getRoute() || "";
		var dlg = openModal(t("Rename"), "ncweb-rename-modal");

		var wrap = el("div", "ncweb-field");
		wrap.appendChild(el("label", "ncweb-label", t("New name")));
		// Files: only the BASE NAME is editable, the extension stays fixed
		// (Gerald 2026-10) - it renders as a fixed label beside the input.
		// Folders (and extensionless names) stay fully editable.
		var parts = entry.is_dir ? { base: entry.name || "", ext: "" } : extSplit(entry.name || "");
		var inputWrap = el("div", "ncweb-input-wrap");
		var input = el("input", "ncweb-input");
		input.type = "text";
		input.value = parts.base;
		input.title = entry.name || "";
		input.placeholder = t("New name");
		inputWrap.appendChild(input);
		if (parts.ext) {
			var extEl = el("span", "ncweb-input-ext", parts.ext);
			inputWrap.appendChild(extEl);
		}
		wrap.appendChild(inputWrap);
		var preview = el("p", "ncweb-hint ncweb-hint-preview");
		wrap.appendChild(preview);
		dlg.body.appendChild(wrap);

		var saveBtn = button(t("Save"), "primary", submit);
		dlg.foot.appendChild(button(t("Cancel"), "secondary", function () { dlg.close(); }));
		dlg.foot.appendChild(saveBtn);

		function sync() {
			var cleaned = cleanName(input.value);
			var full = cleaned ? cleaned + parts.ext : "";
			saveBtn.disabled = !cleaned;
			saveBtn.classList.toggle("ncweb-btn-disabled", !cleaned);
			// The preview always shows the FULL target name (with the locked
			// extension) whenever it differs from the current name.
			preview.textContent = cleaned && full !== entry.name
				? t("Will be renamed to") + ": " + full
				: "";
		}
		input.addEventListener("input", sync);
		input.addEventListener("keydown", function (ev) { if (ev.key === "Enter") submit(); });
		sync();
		setTimeout(function () { input.focus(); input.select(); }, 30);

		function submit() {
			var cleaned = cleanName(input.value);
			if (!cleaned) {
				input.classList.add("ncweb-invalid");
				return;
			}
			saveBtn.disabled = true;
			saveBtn.classList.add("ncweb-btn-disabled");
			var fd = new FormData();
			fd.append("view_id", host.getViewId());
			fd.append("route", route);
			fd.append("name", entry.name);
			// Locked extension: send the FULL name (base + fixed suffix).
			fd.append("new_name", cleaned + parts.ext);
			fetch(host.curl() + host.getRenameMethod(), {
				method: "POST",
				credentials: "same-origin",
				headers: { "X-Frappe-CSRF-Token": host.csrf() },
				body: fd
			}).then(function (r) { return r.json(); }).then(function (res) {
				if (res && res.message && res.message.ok) {
					dlg.close();
					host.showAlert(t("Renamed to") + ": " + res.message.name, "green");
					host.reload();
					return;
				}
				showModalError(dlg, res ? host.exceptionMessage(res) : t("The item could not be renamed."));
				saveBtn.disabled = false;
				saveBtn.classList.remove("ncweb-btn-disabled");
			}).catch(function (err) {
				showModalError(dlg, String(err));
				saveBtn.disabled = false;
				saveBtn.classList.remove("ncweb-btn-disabled");
			});
		}
	}

	function deleteItemConfirm(entry) {
		if (!host.can("delete")) return;
		var route = host.getRoute() || "";
		var question = t("Really delete {0}?").replace("{0}", entry.name)
			+ "\n\n" + t("This cannot be undone.");
		if (!window.confirm(question)) return;

		var fd = new FormData();
		fd.append("view_id", host.getViewId());
		fd.append("route", route);
		fd.append("name", entry.name);
		fetch(host.curl() + host.getDeleteMethod(), {
			method: "POST",
			credentials: "same-origin",
			headers: { "X-Frappe-CSRF-Token": host.csrf() },
			body: fd
		}).then(function (r) { return r.json(); }).then(function (res) {
			if (res && res.message && res.message.ok) {
				host.showAlert(t("Deleted") + ": " + res.message.name, "green");
				host.reload();
				return;
			}
			host.showAlert(res ? host.exceptionMessage(res) : t("The item could not be deleted."), "red");
		}).catch(function (err) {
			host.showAlert(String(err), "red");
		});
	}

	// Error line inside a modal (same shape as the new-folder dialog).
	function showModalError(dlg, msg) {
		var box = dlg.card.querySelector(".ncweb-modal-error");
		if (!box) {
			box = el("div", "ncweb-modal-error");
			dlg.body.appendChild(box);
		}
		box.textContent = msg;
	}

	// ==================================================================
	// boot
	// ==================================================================
	function init(api) {
		host = api || window.NCWeb;
		if (!host) return;
		buildNewButton();
		syncNewVisibility();
	}

	window.NCWebUpload = {
		init: init,
		onFolderLoaded: function (state) {
			if (!state) return;
			if (state.name_rules) nameRules = state.name_rules;
			if (state.max_upload_mb !== undefined) maxUploadMb = Number(state.max_upload_mb) || 0;
			syncNewVisibility();
		},
		// Row/tile menu items for this extension (called by nextcloud_web_view.js).
		extendItemMenu: extendItemMenu,
		// exposed for tests / debugging
		cleanName: cleanName
	};

	if (window.NCWeb) {
		init(window.NCWeb);
	} else {
		document.addEventListener("ncweb:ready", function (ev) { init(ev.detail); });
	}
})();
